import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Trophy, Plus, Trash2, Loader2, Award, Gift } from 'lucide-react';
import { toast } from 'sonner';
import CurriculumEditor from './CurriculumEditor';
import GroupEventsPanel from './GroupEventsPanel';
import {
  getCoachXpSettings, updateCoachXpSettings,
  listCoachChallenges, createCoachChallenge, deleteCoachChallenge,
  coachPendingPromotions, coachPromote,
  getDgPointsBalance,
} from '../lib/api';

const BELT_FIELDS = [
  { key: 'white_to_blue', label: 'White → Blue' },
  { key: 'blue_to_purple', label: 'Blue → Purple' },
  { key: 'purple_to_brown', label: 'Purple → Brown' },
  { key: 'brown_to_black', label: 'Brown → Black' },
];

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const CoachToolsPanel = () => {
  const [balance, setBalance] = useState(0);
  const [settings, setSettings] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [newChallenge, setNewChallenge] = useState({ description: '', xp_reward: 25 });
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, c, p, b] = await Promise.all([
        getCoachXpSettings(), listCoachChallenges(), coachPendingPromotions(), getDgPointsBalance(),
      ]);
      setSettings(s.data);
      setChallenges(c.data || []);
      setPending(p.data || []);
      setBalance(b.data?.balance || 0);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    if (!settings) return;
    const sx = clamp(Number(settings.session_xp) || 25, 1, 100);
    const bt = { ...settings.belt_thresholds };
    for (const k of Object.keys(bt)) bt[k] = clamp(Number(bt[k]) || 1, 1, 500);
    setBusy(true);
    try {
      await updateCoachXpSettings({ session_xp: sx, belt_thresholds: bt });
      toast.success('XP settings saved');
      setSettings({ session_xp: sx, belt_thresholds: bt });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const addChallenge = async () => {
    const xp = clamp(Number(newChallenge.xp_reward) || 1, 1, 100);
    if (!newChallenge.description.trim()) { toast.error('Description required'); return; }
    try {
      await createCoachChallenge(newChallenge.description.trim(), xp);
      setNewChallenge({ description: '', xp_reward: 25 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create challenge');
    }
  };

  const removeChallenge = async (id) => {
    if (!window.confirm('Delete this challenge?')) return;
    try { await deleteCoachChallenge(id); load(); }
    catch { toast.error('Delete failed'); }
  };

  const promote = async (row, action) => {
    try {
      await coachPromote({ client_id: row.client_id, discipline: row.discipline, action });
      toast.success(action === 'approve' ? `Promoted ${row.client_name}` : 'Action saved');
      load();
    } catch { toast.error('Action failed'); }
  };

  return (
    <div className="space-y-6" data-testid="coach-tools-panel">
      {/* DG Points balance */}
      <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-5 flex items-center justify-between" data-testid="coach-dgpoints">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#C0A062]/10 border border-[#C0A062]/40 flex items-center justify-center">
            <Gift className="w-5 h-5 text-[#C0A062]" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">DG Points</p>
            <p className="text-2xl font-bold text-white">{balance.toLocaleString()}</p>
          </div>
        </div>
        <p className="text-xs text-zinc-500 max-w-xs text-right">Top coaches by 30-day DG Points earn the Featured badge.</p>
      </div>

      {/* XP settings */}
      {settings && (
        <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="xp-settings-card">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-5 h-5 text-[#C0A062]" />
            <h2 className="text-lg font-bold text-white">Belt XP settings</h2>
          </div>
          <p className="text-xs text-zinc-500 mb-5">XP is per-discipline, per-coach. Each client tracks separate progress with you.</p>

          <div className="grid sm:grid-cols-2 gap-4 mb-5">
            <div>
              <Label className="text-zinc-300">XP per completed session</Label>
              <Input
                type="number" min="1" max="100"
                value={settings.session_xp}
                onChange={(e) => setSettings({ ...settings, session_xp: e.target.value })}
                className="bg-[#09090B] border-zinc-700 text-white mt-1.5"
                data-testid="session-xp-input"
              />
              <p className="text-[11px] text-zinc-500 mt-1">Range 1–100. Applies to all your clients.</p>
            </div>
          </div>

          <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2">XP to next belt</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {BELT_FIELDS.map(({ key, label }) => (
              <div key={key}>
                <Label className="text-xs text-zinc-400">{label}</Label>
                <Input
                  type="number" min="1" max="500"
                  value={settings.belt_thresholds?.[key] ?? ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    belt_thresholds: { ...settings.belt_thresholds, [key]: e.target.value },
                  })}
                  className="bg-[#09090B] border-zinc-700 text-white mt-1"
                  data-testid={`threshold-${key}`}
                />
              </div>
            ))}
          </div>
          <p className="text-[11px] text-zinc-500 mt-2">Hard cap: 500 XP per belt step.</p>

          <Button onClick={saveSettings} disabled={busy} className="mt-5 bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold" data-testid="save-xp-settings-btn">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save XP settings
          </Button>
        </div>
      )}

      {/* Challenges */}
      <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="challenges-card">
        <h2 className="text-lg font-bold text-white mb-1">Challenges</h2>
        <p className="text-xs text-zinc-500 mb-4">Create challenges your clients can earn XP from. Hard cap 100 XP per challenge.</p>

        <div className="grid sm:grid-cols-[1fr_120px_auto] gap-2 mb-4 items-end">
          <Textarea
            rows={2}
            value={newChallenge.description}
            onChange={(e) => setNewChallenge({ ...newChallenge, description: e.target.value })}
            placeholder="e.g. Drill the armbar setup 50 reps"
            className="bg-[#09090B] border-zinc-700 text-white"
            data-testid="new-challenge-desc"
          />
          <div>
            <Label className="text-xs text-zinc-400">XP</Label>
            <Input
              type="number" min="1" max="100"
              value={newChallenge.xp_reward}
              onChange={(e) => setNewChallenge({ ...newChallenge, xp_reward: e.target.value })}
              className="bg-[#09090B] border-zinc-700 text-white"
              data-testid="new-challenge-xp"
            />
          </div>
          <Button onClick={addChallenge} className="bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold" data-testid="add-challenge-btn">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>

        {challenges.length === 0 ? (
          <p className="text-sm text-zinc-500">No challenges yet.</p>
        ) : (
          <ul className="space-y-2" data-testid="challenges-list">
            {challenges.map((c) => (
              <li key={c.id} className="flex items-start justify-between gap-3 bg-[#0d0d10] border border-zinc-800 rounded-md p-3" data-testid={`challenge-${c.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-zinc-200 break-words">{c.description}</p>
                  <p className="text-[#C0A062] text-xs font-semibold mt-1">+{c.xp_reward} XP</p>
                </div>
                <button onClick={() => removeChallenge(c.id)} className="text-zinc-500 hover:text-red-400 p-1" aria-label="Delete" data-testid={`delete-challenge-${c.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Curriculum editor */}
      <CurriculumEditor />

      {/* Group sessions & seminars */}
      <GroupEventsPanel />

      {/* Pending promotions */}
      <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="pending-promotions-card">
        <div className="flex items-center gap-2 mb-3">
          <Award className="w-5 h-5 text-[#C0A062]" />
          <h2 className="text-lg font-bold text-white">Belt promotion requests</h2>
        </div>
        {pending.length === 0 ? (
          <p className="text-sm text-zinc-500">No clients waiting for a promotion right now.</p>
        ) : (
          <ul className="space-y-2" data-testid="pending-promotions-list">
            {pending.map((row, i) => (
              <li key={i} className="flex items-center justify-between gap-3 bg-[#0d0d10] border border-zinc-800 rounded-md p-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-white">{row.client_name}</p>
                  <p className="text-xs text-zinc-500">
                    {row.discipline.replace('_', ' ')} · current {row.current_belt} · {row.xp} XP · {row.sessions_completed} sessions
                  </p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <Button size="sm" className="bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 border border-emerald-500/40" onClick={() => promote(row, 'approve')} data-testid={`promote-approve-${row.client_id}-${row.discipline}`}>
                    Approve
                  </Button>
                  <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-white" onClick={() => promote(row, 'hold')} data-testid={`promote-hold-${row.client_id}-${row.discipline}`}>
                    Hold
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CoachToolsPanel;
